import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { BellRing, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { pushStatus, pushVapidKey, pushSubscribe, pushUnsubscribe, pushTest } from "../lib/api";

const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const toKey = (b64) => { const s = atob((b64 + "=".repeat((4 - (b64.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from(s, (c) => c.charCodeAt(0)); };
const registration = async () => navigator.serviceWorker.register("/push-sw.js", { scope: "/push/" }).then(() => navigator.serviceWorker.getRegistration("/push/"));
const localSubscription = async () => { try { const reg = await navigator.serviceWorker.getRegistration("/push/"); return (await reg?.pushManager.getSubscription()) || null; } catch { return null; } };

// Reminders panel: browser notifications with what's expiring — same Monday / ≤3-day cadence as the texts, no phone plan needed.
// The checkbox reflects THIS browser; other devices show as a count.
export const PushReminderToggle = () => {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [local, setLocal] = useState(null);
  const { data } = useQuery({ queryKey: ["push-reminders"], queryFn: pushStatus, staleTime: 60_000 });
  const ok = supported();
  useEffect(() => { if (ok) localSubscription().then((s) => setLocal(s?.endpoint || "")); }, [ok, data]);
  if (!data) return null;
  const denied = ok && Notification.permission === "denied";
  const here = !!local && (data.subscriptions || []).some((s) => local.endsWith(s.endpoint_hint));
  const others = data.devices - (here ? 1 : 0);
  const enable = async () => {
    setBusy(true);
    try {
      if ((await Notification.requestPermission()) !== "granted") { toast.error("Notifications are blocked for this site — allow them in the address bar, then try again"); return; }
      const reg = await registration();
      const { public_key } = await pushVapidKey();
      const sub = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(public_key) }));
      const r = await pushSubscribe({ ...sub.toJSON(), label: navigator.userAgent.slice(0, 80) });
      qc.setQueryData(["push-reminders"], r);
      setLocal(sub.endpoint);
      toast.success("This device will get a notification when something is expiring");
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't turn on notifications here"); }
    finally { setBusy(false); }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const sub = await localSubscription();
      const endpoint = sub?.endpoint || local;
      await sub?.unsubscribe();
      if (endpoint) qc.setQueryData(["push-reminders"], await pushUnsubscribe(endpoint));
      setLocal("");
      toast.success("Browser reminders off on this device");
    } catch (e) { toast.error("Couldn't turn that off"); }
    finally { setBusy(false); }
  };
  const test = async () => { try { await pushTest(); toast.success("Test notification sent"); } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send a test"); } };
  const hint = !ok ? "This browser can't receive notifications — on iPhone, add HearthVault to your Home Screen first."
    : denied ? "Notifications are blocked for this site in your browser settings."
    : here ? `On for this device${others > 0 ? ` and ${others} other${others === 1 ? "" : "s"}` : ""}. Monday mornings when renewals or expiries fall in the next 7 days — and sooner if something is 3 days out. ${data.upcoming} coming up now. Emergency-access alerts arrive here too.`
    : `${others > 0 ? `On for ${others} other device${others === 1 ? "" : "s"}, not this one. ` : ""}A browser notification on Monday mornings when something expires in the next 7 days — and sooner if it's 3 days out. Owners are also alerted when someone requests emergency access; executors when a request is approved, released or denied. Works without a text plan.`;
  return (
    <div className="mt-2 flex items-start gap-2 rounded-xl border border-border bg-card p-3 text-sm" data-testid="push-reminders-toggle">
      <input type="checkbox" className="mt-1 accent-primary" checked={here} disabled={!ok || denied || busy} onChange={(e) => (e.target.checked ? enable() : disable())} data-testid="push-reminders-checkbox" />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 font-medium"><BellRing className="h-3.5 w-3.5 text-primary" /> Notify this device what's expiring {busy && <Loader2 className="h-3 w-3 animate-spin" />}</span>
        <span className="block text-xs text-muted-foreground mt-0.5" data-testid="push-reminders-hint">{hint}</span>
        {here && <button type="button" onClick={test} className="mt-1 text-xs text-primary hover:underline" data-testid="push-reminders-test-btn">Send a test</button>}
      </span>
    </div>
  );
};
